import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { getSupabaseClient, isSupabaseAuthEnabled } from '@/lib/supabase';
import { requiresPasswordReset } from '@/features/auth/lib/password-reset';
import { useSetPassword } from '@/features/auth/api/useSupabaseAuth';
import { AuthMarketingShell } from '@/features/auth/components/AuthMarketingShell';
import { AuthTextField } from '@/features/auth/components/AuthTextField';

const MIN_PASSWORD_LENGTH = 8;
const SESSION_STALE_MS = 60_000;

const setPasswordSchema = z
  .object({
    password: z
      .string()
      .min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters`)
      .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Must contain at least one number'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type SetPasswordFormData = z.infer<typeof setPasswordSchema>;

/**
 * Set-password screen for migrated users.
 *
 * Reached two ways: (1) a reset-required user (Phase 7, no recoverable
 * password) follows the Supabase recovery/activation email — the link lands
 * here with a recovery session; (2) the post-auth router sends a session whose
 * `app_metadata.requires_password_reset` is true here before the app loads
 * (`nextRouteAfterAuth`). Submitting calls `supabase.auth.updateUser` so the
 * user ends up with a real password.
 *
 * NOTE: clearing the `requires_password_reset` provenance flag itself lives in
 * `app_metadata`, which only a service-role call can mutate. A backend
 * endpoint to clear it after a successful set is a tracked follow-up (it is a
 * new auth surface and needs sign-off per `security.mdc` §3.1); until then the
 * flag is harmless once the user has a password.
 */
export function SetPasswordPage() {
  const navigate = useNavigate();
  const setPassword = useSetPassword();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const enabled = isSupabaseAuthEnabled();
  const { data: session, isLoading: sessionLoading } = useQuery({
    queryKey: ['supabase', 'session'],
    queryFn: async () => {
      const { data } = await getSupabaseClient().auth.getSession();
      return data.session;
    },
    enabled,
    staleTime: SESSION_STALE_MS,
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SetPasswordFormData>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });
  const passwordValue = watch('password') ?? '';
  const confirmPasswordValue = watch('confirmPassword') ?? '';

  const isActivation = requiresPasswordReset(session);
  const email = session?.user.email ?? '';

  const handleSetPassword = handleSubmit((data) => {
    setPassword.mutate({ password: data.password });
  });

  const handleGoToSignIn = () => {
    navigate('/login');
  };

  if (!enabled) {
    return (
      <AuthMarketingShell title="Not available">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            Password setup runs through your normal sign-in. Return to the sign-in page.
          </p>
          <div className="mt-6">
            <BackToSignIn />
          </div>
        </div>
      </AuthMarketingShell>
    );
  }

  if (sessionLoading) {
    return <AuthMarketingShell isLoading />;
  }

  if (!session) {
    return (
      <AuthMarketingShell
        title="Link expired or invalid"
        subtitle="This activation link is invalid or has expired. Request a new one to set your password."
      >
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-warning-bg">
            <AlertCircle className="h-6 w-6 text-warning" />
          </div>
          <div className="mt-2 flex flex-col gap-3">
            <Link
              to="/forgot-password"
              className="rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Request a new link
            </Link>
            <BackToSignIn />
          </div>
        </div>
      </AuthMarketingShell>
    );
  }

  if (setPassword.isSuccess) {
    return (
      <AuthMarketingShell title="Password set" subtitle="Your password is ready. You can now sign in with it.">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
            <CheckCircle className="h-6 w-6 text-primary" />
          </div>
          <button
            type="button"
            onClick={handleGoToSignIn}
            className="mt-2 w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Sign in
          </button>
        </div>
      </AuthMarketingShell>
    );
  }

  return (
    <AuthMarketingShell
      title={isActivation ? 'Activate your account' : 'Set a new password'}
      subtitle={
        isActivation
          ? `Welcome to TruSpace. Choose a password to finish setting up ${email}`
          : `Choose a strong password for ${email}`
      }
    >
      <form
        onSubmit={handleSetPassword}
        autoComplete="off"
        className="flex flex-col gap-5"
      >
        {setPassword.isError && (
          <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive-text" role="alert">
            Unable to set your password. The link may have expired — request a new one.
          </div>
        )}

        <div>
          <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-foreground">
            New password
          </label>
          <AuthTextField
            {...register('password')}
            id="password"
            autoComplete="off"
            autoFocus
            value={passwordValue}
            onClear={() =>
              setValue('password', '', { shouldDirty: true, shouldValidate: true })
            }
            clearLabel="Clear password"
            passwordToggle
            isPasswordVisible={showPassword}
            onTogglePasswordVisibility={() => setShowPassword((v) => !v)}
            isInvalid={Boolean(errors.password)}
            aria-describedby={errors.password ? 'password-error' : undefined}
            placeholder="At least 8 characters"
          />
          {errors.password && (
            <p id="password-error" className="mt-1 text-xs text-destructive-text">
              {errors.password.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="mb-1.5 block text-sm font-medium text-foreground">
            Confirm new password
          </label>
          <AuthTextField
            {...register('confirmPassword')}
            id="confirmPassword"
            autoComplete="off"
            value={confirmPasswordValue}
            onClear={() =>
              setValue('confirmPassword', '', { shouldDirty: true, shouldValidate: true })
            }
            clearLabel="Clear confirm password"
            passwordToggle
            isPasswordVisible={showConfirmPassword}
            onTogglePasswordVisibility={() => setShowConfirmPassword((v) => !v)}
            isInvalid={Boolean(errors.confirmPassword)}
            aria-describedby={errors.confirmPassword ? 'confirm-error' : undefined}
            placeholder="Re-enter your password"
          />
          {errors.confirmPassword && (
            <p id="confirm-error" className="mt-1 text-xs text-destructive-text">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        <div className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
          <p className="font-medium text-muted-foreground">Password requirements:</p>
          <ul className="mt-1 list-inside list-disc space-y-0.5">
            <li>At least 8 characters</li>
            <li>One uppercase letter</li>
            <li>One lowercase letter</li>
            <li>One number</li>
          </ul>
        </div>

        <button
          type="submit"
          disabled={setPassword.isPending}
          className="rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {setPassword.isPending ? 'Saving…' : 'Set password'}
        </button>

        <BackToSignIn />
      </form>
    </AuthMarketingShell>
  );
}

function BackToSignIn() {
  return (
    <Link
      to="/login"
      className="inline-flex items-center justify-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to sign in
    </Link>
  );
}
